import {transparentize} from 'polished';
import React, {FC, ReactNode, useState} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../../libs/shared/core/src/helpers/internationalization/react';
import {RendererOf} from '../../../../../libs/shared/core/src/helpers/react/reactHelpers';
import {IconButton} from '../../../../../libs/shared/ui/src/components/buttons/iconButton';
import {fontSizes, fontWeights} from '../../../../../libs/shared/ui/src/styles/fontStyles';
import {UserThemeConsumer} from '../../../../../libs/shared/ui/src/theme/userThemeConsumer';
import {CrmSyncStatusesEnum} from '../../../../models/api/crmSyncModel';
import {EditInCrmButtonContainer} from '../../../common/crm/buttons/editInCrmButtonContainer';
import {InlineEditingProps, useInlineEditing} from '../../../common/inlineEditing/useInlineEditing';

/*
 * Props.
 */

interface AccountInformationAttributeProps<T> {
  title: ReactNode;
  currentAttributeValue: T;
  shouldStartInEditMode: boolean;
  isFieldCrmSyncing: boolean;
  crmSyncEditLink?: string;
  crmSyncStatus?: CrmSyncStatusesEnum;
  crmSyncSource?: string;
  shouldDisableActions?: boolean;
  renderValue: RendererOf<InlineEditingProps<T>>;
  onSave: (arg: T) => Promise<void>;
  onDelete: () => void;
}

/*
 * Style.
 */

const StyledContentDiv = styled.div`
  margin: 10px 0;

  &:last-child {
    margin-bottom: 0;
  }
`;

const StyledTitleDiv = styled.div`
  font-size: ${fontSizes.small};
  font-weight: ${fontWeights.bold};
`;

const StyledValueDiv = styled.div`
  font-size: ${fontSizes.medium};
  position: relative;
  display: flex;
  align-items: center;
  overflow-wrap: anywhere;

  /* Overrides the p formatting that comes with LinkifyRenderer that some values use */
  p {
    margin: 0;
  }

  a {
    color: ${(p) => p.theme.interactiveText};
    cursor: pointer;
  }
`;

const StyledControlsDiv = styled.div`
  position: absolute;
  right: 0px;
  top: 4px;
  padding-left: 16px;
  background: linear-gradient(
    to right,
    ${(p) => transparentize(1, p.theme.foreground)} 0%,
    ${(p) => p.theme.foreground} 16px
  );
  display: flex;
`;

const StyledEditInCrmButtonContainer = styled(EditInCrmButtonContainer)`
  margin-top: -4px;
`;

/*
 * Component.
 */

export const AccountInformationAttribute = <T = string,>(
  props: AccountInformationAttributeProps<T>,
): ReturnType<FC> => {
  const {
    title,
    currentAttributeValue,
    shouldStartInEditMode,
    isFieldCrmSyncing,
    crmSyncEditLink,
    crmSyncStatus,
    crmSyncSource,
    shouldDisableActions,
    renderValue,
    onSave,
    onDelete,
  } = props;

  const [shouldShowControls, setShouldShowControls] = useState(false);
  const inlineEditingProps = useInlineEditing<T>({
    currentAttributeValue,
    shouldStartInEditMode,
    onSave,
  });

  const showControls = () => {
    if (inlineEditingProps.isEditing || inlineEditingProps.isSaving || shouldDisableActions) {
      return;
    }

    setShouldShowControls(true);
  };

  const hideControls = () => {
    if (shouldShowControls) {
      setShouldShowControls(false);
    }
  };

  const startEditing = () => {
    hideControls();
    inlineEditingProps.onEditStart();
  };

  return (
    <StyledContentDiv>
      <StyledTitleDiv>{title}</StyledTitleDiv>
      <StyledValueDiv onMouseEnter={showControls} onMouseMove={showControls} onMouseLeave={hideControls}>
        {renderValue(inlineEditingProps)}
        {shouldShowControls && (
          <StyledControlsDiv>
            {renderEditButtonOrLinkToCrm(
              startEditing,
              onDelete,
              isFieldCrmSyncing,
              crmSyncEditLink,
              crmSyncSource,
              crmSyncStatus,
            )}
          </StyledControlsDiv>
        )}
      </StyledValueDiv>
    </StyledContentDiv>
  );
};

/*
 * Helpers.
 */

function renderEditButtonOrLinkToCrm(
  onEditClick: () => void,
  onDeleteClick: () => void,
  isFieldCrmSyncing: boolean,
  crmSyncEditLink?: string,
  crmSyncSource?: string,
  crmSyncStatus?: CrmSyncStatusesEnum,
) {
  if (!isFieldCrmSyncing || !crmSyncSource || crmSyncStatus === CrmSyncStatusesEnum.DISABLED) {
    return (
      <>
        <UserThemeConsumer>
          {(theme) => (
            <IconButton
              iconName="editSmall"
              color={theme.greys.shade70}
              label={<Trans id="account-details-account-information-attribute-edit">Edit</Trans>}
              onClick={onEditClick}
            />
          )}
        </UserThemeConsumer>
        <UserThemeConsumer>
          {(theme) => (
            <IconButton
              iconName="trashSmall"
              color={theme.greys.shade70}
              label={<Trans id="account-details-account-information-attribute-delete">Delete</Trans>}
              onClick={onDeleteClick}
            />
          )}
        </UserThemeConsumer>
      </>
    );
  }
  if (crmSyncEditLink) {
    return <StyledEditInCrmButtonContainer source={crmSyncSource} linkUrl={crmSyncEditLink} />;
  }
  return null;
}
